import { useState } from "react";
import { useWorkflowBuilder } from "V2.0/HomePages/Workflows/Builder/index2";
import { useBuilder } from "V2.0/HomePages/Workflows/Builder";
import { useReactFlow } from "@xyflow/react";
import {
  addCommBinding,
  updatePipeline,
} from "V2.0/HomePages/Workflows/service";
import InputComponent from "V2.0/components/InputComponent";
import { Button } from "V2.0/common/button";
import { message } from "antd";
import { Table, TableBody, TableRow, TableCell } from "V2.0/common/table";
import { TrashIcon } from "@heroicons/react/24/outline";
import { Dialog, DialogActions, DialogTitle } from "V2.0/common/dialog";
import { showErrorToast } from "V2.0/utils";

const defaultOption = {
  id: 0,
  value: "",
  label: "Please Select a phone number",
};

function PhoneNumberSelect(props) {
  const { phoneNumbers, onAddPhoneNumber } = props;

  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState("");
  const [loading, setLoading] = useState(false);

  const phoneNumberOptions = [
    defaultOption,
    ...phoneNumbers.map((number, idx) => {
      return { id: idx + 1, value: number, label: number };
    }),
  ];

  const isAddDisabled = !Boolean(selectedPhoneNumber);

  const onAddPhoneNumberInit = () => setLoading(true);

  const onAddPhoneNumberSuccess = () => {
    setLoading(false);
    message.success("Phone Number Added");
    setSelectedPhoneNumber("");
  };

  const onAddPhoneNumberError = (e) => {
    setLoading(false);
    showErrorToast(e, "Failed to add phone number");
  };

  const lifecycleCbs = {
    onInit: onAddPhoneNumberInit,
    onSuccess: onAddPhoneNumberSuccess,
    onError: onAddPhoneNumberError,
  };

  return (
    <div className="flex items-center w-full gap-x-2">
      <InputComponent
        type="select"
        options={phoneNumberOptions}
        value={selectedPhoneNumber}
        onChange={(e) => setSelectedPhoneNumber(e.target.value)}
      />
      <div>
        <Button
          loading={loading}
          disabled={isAddDisabled}
          onClick={() => onAddPhoneNumber(selectedPhoneNumber, lifecycleCbs)}
        >
          Add
        </Button>
      </div>
    </div>
  );
}

function PhoneNumberRemove(props) {
  const { phoneNumber, onRemovePhoneNumber, onClose } = props;

  const [loading, setLoading] = useState(false);

  const onInit = () => setLoading(true);
  const onSuccess = () => {
    setLoading(false);
    message.success("Phone Number Removed");
    onClose();
  };
  const onError = (e) => {
    setLoading(false);
    showErrorToast(e, "Failed to remove number");
  };

  const lifecycleCbs = {
    onInit,
    onSuccess,
    onError,
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
    >
      <DialogTitle>Remove {phoneNumber} ?</DialogTitle>
      <DialogActions>
        <Button
          outline
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          loading={loading}
          onClick={() => onRemovePhoneNumber(lifecycleCbs)}
        >
          Remove
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PhoneNumberSelected(props) {
  const { phoneNumbers, onRemovePhoneNumber } = props;
  const [openPhoneNumberRemove, setOpenPhoneNumberRemove] = useState({
    isOpen: false,
    phoneNumber: "",
  });

  const onPhoneNumberRemoveClose = () =>
    setOpenPhoneNumberRemove({
      isOpen: false,
      phoneNumber: "",
    });

  const onPhoneNumberRemoveOpen = (phoneNumber) => {
    setOpenPhoneNumberRemove({
      isOpen: true,
      phoneNumber,
    });
  };

  return (
    <>
      <div className="flex flex-col gap-y-2">
        <p className="font-semibold text-sm">
          Connected ({phoneNumbers.length})
        </p>
        {phoneNumbers.length > 0 && (
          <Table>
            <TableBody>
              {phoneNumbers.map((phoneNumber, idx) => {
                return (
                  <TableRow>
                    <TableCell>{phoneNumber}</TableCell>
                    <TableCell>
                      <Button
                        outline
                        onClick={() => onPhoneNumberRemoveOpen(phoneNumber)}
                      >
                        <TrashIcon />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
      {openPhoneNumberRemove.isOpen && (
        <PhoneNumberRemove
          onClose={onPhoneNumberRemoveClose}
          phoneNumber={openPhoneNumberRemove.phoneNumber}
          onRemovePhoneNumber={(lifecycleCbs) =>
            onRemovePhoneNumber(openPhoneNumberRemove.phoneNumber, lifecycleCbs)
          }
        />
      )}
    </>
  );
}

function TriggerNodeSettings(props) {
  const { node, setNodes, notifyChange, phoneNumbers, channelType } = props;
  const [internalNode, setInternalNode] = useState(node);
  const { pipelineId, workspaceId } = useWorkflowBuilder();
  const { transformNodes, transformEdges } = useBuilder();
  const { getNodes, getEdges } = useReactFlow();

  const communicationBinding =
    internalNode?.data?.settings?.communicationBinding;

  const bindedPhoneNumbers = communicationBinding.reduce((acc, binding) => {
    return {
      ...acc,
      [binding.identifier]: true,
    };
  }, {});

  const availablePhoneNumbers = phoneNumbers.filter(
    (phoneNumber) => !bindedPhoneNumbers[phoneNumber]
  );

  const isPhoneNumbersSelected = communicationBinding.length > 0;

  const findCommunicationBindingIdx = (identifier) => {
    const idx = communicationBinding.findIndex(
      (binding) => binding.identifier === identifier
    );

    return idx;
  };

  const removeIdentifierCommunicationBinding = (identifier) => {
    const idx = findCommunicationBindingIdx(identifier);

    const updatedCommunicationBindings = communicationBinding.toSpliced(idx, 1);
    return updatedCommunicationBindings;
  };

  async function handleSave(identifier, lifecycleCbs) {
    lifecycleCbs.onInit();
    try {
      const payload = {
        channelType,
        isActive: true,
        identifier,
      };
      const response = await addCommBinding({
        workspaceId,
        pipelineId,
        nodeId: internalNode.data._id,
        payload,
      });
      if (response.status === 201) {
        lifecycleCbs.onSuccess();
        const nodes = getNodes();
        let updatedInternalNode;
        const updatedNodes = nodes.map((_node) => {
          if (_node.id === internalNode.id) {
            const { selected, ...rest } = _node;
            const updatedNode = {
              ...rest,
              selected: false,
              data: {
                ...internalNode.data,
                settings: {
                  communicationBinding: [
                    ...internalNode.data.settings.communicationBinding,
                    response.data.binding,
                  ],
                  communicationType: channelType,
                },
              },
            };
            setInternalNode(updatedNode);
            return updatedNode;
          } else {
            return _node;
          }
        });
        setNodes(updatedNodes);
        notifyChange();
      }
    } catch (e) {
      lifecycleCbs.onError(e);
    }
  }

  async function handleRemovePhoneNumber(identifier, lifecycleCbs) {
    lifecycleCbs.onInit();

    try {
      const updatedCommunicationBindings =
        removeIdentifierCommunicationBinding(identifier);
      const nodes = getNodes();
      const edges = getEdges();

      let updatedNode;

      const updatedNodes = nodes.map((_node) => {
        if (_node.id === node.id) {
          const { selected, ...rest } = _node;
          updatedNode = {
            ...rest,
            selected: false,
            data: {
              ...node.data,
              settings: {
                communicationBinding: updatedCommunicationBindings,
                communicationType: channelType,
              },
            },
          };
          return updatedNode;
        } else {
          return _node;
        }
      });

      const transformedNodes = transformNodes(updatedNodes);
      const transformedEdges = transformEdges(edges);

      const payload = {
        nodes: transformedNodes,
        edges: transformedEdges,
      };

      const response = await updatePipeline({
        workspaceId,
        pipelineId,
        payload,
      });

      if (response.status === 200) {
        setNodes(updatedNodes);
        setInternalNode(updatedNode);
        lifecycleCbs.onSuccess();
      }
    } catch (e) {
      lifecycleCbs.onError();
    }
  }

  return (
    <div className="flex flex-col gap-y-4">
      <p className="font-semibold text-sm">Phone Numbers</p>
      <PhoneNumberSelect
        phoneNumbers={availablePhoneNumbers}
        onAddPhoneNumber={handleSave}
      />
      {isPhoneNumbersSelected && (
        <PhoneNumberSelected
          phoneNumbers={Object.keys(bindedPhoneNumbers)}
          onRemovePhoneNumber={handleRemovePhoneNumber}
        />
      )}
    </div>
  );
}

export { TriggerNodeSettings };
